import type { LexData, GovernanceProposal } from "./types";
import {
  activeDelegationsForProposal,
  allocateFirmVotingParticipants,
  buildDelegatedVoteLines,
  calculateMembershipParticipationBonus,
  calculateFirmAggregateVotes,
  calculateFirmVotingEntitlement,
  calculateVoteResult,
  calculateVotingEntitlement,
  defaultVotingRule,
  hasBindingVotingRights,
  participantVoteToVoteLine,
  votingWeightForMembership,
  type DecisionType,
  type DelegationRecord,
  type MemberFirmVoteProfile,
  type ParticipationPointEntry,
  type ParticipantVote,
  type ProposalVoteConfig,
  type VoteLine,
} from "./voting";

const now = Date.now();
const day = 24 * 60 * 60 * 1000;

export function proposalDecisionType(proposal: GovernanceProposal): DecisionType {
  if (proposal.category === "charter amendment") return "constitutional";
  if (proposal.category === "admission" || proposal.category === "larger boutique admission") return "important";
  if (proposal.category === "suspension" || proposal.category === "exclusion" || proposal.category === "conflict decision") return "sensitive";
  return "ordinary";
}

export function votingFirms(data: LexData): MemberFirmVoteProfile[] {
  return data.firms.map((firm) => {
    const representatives = data.users.filter((user) => user.firmId === firm.id && user.active);
    const principal = representatives.find((user) => user.representativeType === "principal");
    const alternates = representatives.filter((user) => user.representativeType === "alternate").map((user) => user.id);
    return {
      id: firm.id,
      status: firm.status === "active" ? "active" : firm.membershipCategory === "Observer" ? "observer" : "applicant",
      membershipType: firm.membershipCategory === "Observer" ? "observer" : "member_firm",
      membershipCategory: firm.membershipCategory,
      votingRepresentativeUserId: principal?.id ?? null,
      alternateRepresentativeUserIds: alternates,
    };
  });
}

export function proposalVoteConfig(data: LexData, proposal: GovernanceProposal): ProposalVoteConfig {
  const discussion = data.discussions.find((item) => item.id === proposal.discussionId);
  const decisionType = proposalDecisionType(proposal);
  return {
    id: proposal.id,
    title: discussion?.title ?? "Governance proposal",
    finalText: proposal.consolidatedText ?? proposal.proposalText,
    decisionType,
    delegationAllowed: decisionType !== "sensitive" && decisionType !== "constitutional",
    participationWeightingEnabled: decisionType === "ordinary" || decisionType === "important",
    secretBallot: decisionType === "sensitive",
    votingOpensAt: proposal.votingOpensAt,
    votingClosesAt: proposal.votingClosesAt,
  };
}

const PARTICIPATION_PERIOD = "2026-H1";

/**
 * Participation points derived from each firm's REAL governance activity in the
 * current period, plus any admin-validated points awarded out of band. A firm
 * that has done nothing earns nothing and stays at the base voting weight — the
 * earlier index-based demo points are gone. Points feed a capped bonus
 * (see calculateParticipationBonus), so participation raises influence within a
 * limit and can never be gamed past the cap.
 */
export function realParticipationPoints(data: LexData): ParticipationPointEntry[] {
  const period = PARTICIPATION_PERIOD;
  const entries: ParticipationPointEntry[] = [];
  const add = (firmId: string, userId: string, category: ParticipationPointEntry["category"], points: number, description: string) => {
    if (points <= 0) return;
    entries.push({ id: `pp-${firmId}-${category}`, firmId, userId, category, points, period, description, validationStatus: "validated", createdAt: now, createdBy: userId, source: "governance" });
  };
  for (const firm of data.firms) {
    if (firm.membershipCategory === "Observer") continue;
    const repUserId = data.users.find((user) => user.firmId === firm.id)?.id ?? firm.id;
    const votesCast = new Set(data.votes.filter((vote) => vote.firmId === firm.id).map((vote) => vote.proposalId)).size;
    const comments = data.contributions.filter((item) => item.createdByFirmId === firm.id).length;
    const amendments = data.amendments.filter((item) => item.sponsorFirmId === firm.id).length;
    const discussionsStarted = data.discussions.filter((item) => item.createdByFirmId === firm.id).length;
    // EU-affairs credit rewards breadth, not just ownership (B1): projects led, joined, and co-signed.
    const ownedProjectIds = new Set(data.lobbyingProjects.filter((item) => item.ownerFirmId === firm.id).map((item) => item.id));
    const joinedProjectIds = new Set((data.lobbyingProjectParticipants ?? []).filter((participant) => participant.firmId === firm.id).map((participant) => participant.projectId));
    const joinedNotOwned = [...joinedProjectIds].filter((id) => !ownedProjectIds.has(id)).length;
    const endorsedCount = (data.lobbyingProjectEndorsements ?? []).filter((endorsement) => endorsement.firmId === firm.id).length;
    const euAffairsPoints = ownedProjectIds.size * 8 + joinedNotOwned * 4 + endorsedCount * 2;
    add(firm.id, repUserId, "vote_cast", votesCast * 5, "Votes cast in the current period.");
    add(firm.id, repUserId, "proposal_comment", comments * 3, "Contributions to discussions and proposals.");
    add(firm.id, repUserId, "amendment_submitted", amendments * 8, "Amendments sponsored.");
    add(firm.id, repUserId, "proposal_submitted", discussionsStarted * 6, "Discussions, notes and proposals started.");
    add(firm.id, repUserId, "eu_affairs_contribution", euAffairsPoints, "Legislative / EU-affairs work (led, contributed, co-signed).");
  }
  // Merge any admin-validated points awarded out of band (meetings, peer recognition, etc.).
  for (const point of (data.participationPoints ?? []).filter((item) => item.validationStatus === "validated")) {
    entries.push(point);
  }
  return entries;
}

export function demoDelegations(): DelegationRecord[] {
  return [
    {
      id: "deleg-1",
      delegatingFirmId: "firm-2",
      delegateFirmId: "firm-1",
      scopeType: "standing",
      scopeReference: "ordinary",
      validFrom: now - 10 * day,
      validUntil: now + 40 * day,
      status: "active",
      createdAt: now - 10 * day,
    },
    {
      id: "deleg-2",
      delegatingFirmId: "firm-6",
      delegateFirmId: "firm-1",
      scopeType: "vote_specific",
      scopeReference: "prop-1",
      validFrom: now - 2 * day,
      validUntil: now + 8 * day,
      status: "active",
      createdAt: now - 2 * day,
    },
    {
      id: "deleg-3",
      delegatingFirmId: "firm-7",
      delegateFirmId: "firm-2",
      scopeType: "topic_specific",
      scopeReference: "EU affairs",
      validFrom: now - 4 * day,
      validUntil: now + 20 * day,
      status: "active",
      createdAt: now - 4 * day,
    },
  ];
}

export function governanceContext(data: LexData, proposalId: string) {
  const proposal = data.proposals.find((item) => item.id === proposalId);
  const discussion = proposal ? data.discussions.find((item) => item.id === proposal.discussionId) : undefined;
  const firms = votingFirms(data);
  const activeFirmCount = firms.filter((firm) => firm.status === "active").length;
  if (!proposal) return null;

  const config = proposalVoteConfig(data, proposal);
  const rule = defaultVotingRule(config.decisionType);
  const participation = realParticipationPoints(data);
  const delegations = activeDelegationsForProposal({
    delegations: data.delegations.length > 0 ? data.delegations : demoDelegations(),
    proposal: config,
    rule,
    activeFirmCount,
    now,
  });
  const participantAllocations = data.viewerFirm
    ? (() => {
        const profileEntitlement = calculateVotingEntitlement({
          memberEntityId: data.viewerFirm.id,
          membershipCategory: data.viewerFirm.membershipCategory,
          period: "2026-H1",
          participationBonus: calculateMembershipParticipationBonus(participation, data.viewerFirm.id, "2026-H1", data.viewerFirm.membershipCategory),
          decisionType: config.decisionType,
          associateLimitedVotingEnabled: rule.associateLimitedVotingEnabled,
          expertLimitedVotingEnabled: rule.expertLimitedVotingEnabled,
        });
        return allocateFirmVotingParticipants({
          entitlement: calculateFirmVotingEntitlement({
            firmId: data.viewerFirm.id,
            period: "2026-H1",
            participationBonus: profileEntitlement.participationBonus,
            baseWeight: profileEntitlement.baseWeight,
            maxFirmWeight: profileEntitlement.maxTotalWeight,
          }),
          userIds: [data.viewer.id],
          decisionId: proposal.id,
          rule,
        });
      })()
    : [];
  const persistedVotes = data.votes.filter((vote) => vote.proposalId === proposal.id && vote.validAtClose !== false);
  const directVotes = persistedVotes.length > 0 ? persistedVotes : demoVoteLines(data, config, participation);

  // Transfer delegated voting weight onto the delegates' votes so procurations
  // actually count, bounded by the sliding holding cap already applied above.
  const firmWeightFor = (firmId: string) => {
    const firm = data.firms.find((item) => item.id === firmId);
    if (!firm) return 0;
    return votingWeightForMembership({
      membershipCategory: firm.membershipCategory,
      participationBonus: calculateMembershipParticipationBonus(participation, firmId, "2026-H1", firm.membershipCategory),
      decisionType: config.decisionType,
      participationWeightingEnabled: config.participationWeightingEnabled,
      rule,
    });
  };
  const { tallyVotes, delegatedLines, suppressedFirmIds } = buildDelegatedVoteLines({ proposal: config, rule, delegations, votes: directVotes, firmWeightFor });
  const votes = tallyVotes;
  const firmAggregates = calculateFirmAggregateVotes({ decisionId: config.id, votes });
  const certifiedSnapshot = data.voteResultSnapshots
    .filter((snapshot) => snapshot.proposalId === proposal.id)
    .sort((a, b) => (b.certifiedAt ?? b.createdAt) - (a.certifiedAt ?? a.createdAt))[0];
  const result = certifiedSnapshot ?? calculateVoteResult({ proposal: config, firms, votes, rule, certifiedBy: null, certifiedAt: null });

  return { proposal, discussion, firms, activeFirmCount, config, rule, participation, delegations, delegatedLines, suppressedFirmIds, participantAllocations, firmAggregates, directVotes, votes, result, certifiedSnapshot };
}

function demoVoteLines(data: LexData, config: ProposalVoteConfig, participation: ParticipationPointEntry[]): VoteLine[] {
  const period = "2026-H1";
  const votingFirms = data.firms.filter((firm) =>
    hasBindingVotingRights({
      membershipCategory: firm.membershipCategory,
      decisionType: config.decisionType,
    }),
  );
  const votes: VoteLine[] = [];
  const values = ["strong_support", "support", "object_unless_amended", "abstain", "support_with_reservations", "oppose"] as const;

  votingFirms.forEach((firm, index) => {
    const user = data.users.find((item) => item.firmId === firm.id) ?? data.viewer;
    const participationBonus = calculateMembershipParticipationBonus(participation, firm.id, period, firm.membershipCategory);
    const firmWeight = votingWeightForMembership({
      membershipCategory: firm.membershipCategory,
      participationBonus,
      decisionType: config.decisionType,
      participationWeightingEnabled: config.participationWeightingEnabled,
    });

    if (config.decisionType === "ordinary" && index === 0) {
      const participantVotes: ParticipantVote[] = [
        { id: `vote-${firm.id}-p1`, decisionId: config.id, firmId: firm.id, userId: user.id, allocatedWeight: firmWeight / 2, voteValue: "strong_support", castAt: Math.min(now - 5 * 60 * 60 * 1000, config.votingClosesAt - 60_000) },
        { id: `vote-${firm.id}-p2`, decisionId: config.id, firmId: firm.id, userId: `${user.id}-alternate`, allocatedWeight: firmWeight / 2, voteValue: "object_unless_amended", castAt: Math.min(now - 4 * 60 * 60 * 1000, config.votingClosesAt - 60_000) },
      ];
      votes.push(...participantVotes.map((line) => participantVoteToVoteLine(line)));
    } else {
      votes.push({
        id: `vote-${firm.id}`,
        proposalId: config.id,
        firmId: firm.id,
        userId: user.id,
        voteValue: values[index % values.length],
        votingWeight: firmWeight,
        isDelegated: false,
        delegatedFromFirmId: null,
        createdAt: Math.min(now - (6 - index) * 60 * 60 * 1000, config.votingClosesAt - 60_000),
      });
    }
  });

  return votes;
}
